import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation, useSearchParams } from "react-router-dom";
import { ArrowLeft, Briefcase, IndianRupee, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { AgentActionChatPanel } from "@/components/AgentActionChat";

/**
 * Per-agent page — embeds the AgentActionChatPanel so visitors see the
 * agent working through its 24/7 loop (scanning → pitching → negotiating →
 * review → payout) instead of a free-form chat.
 *
 * If the user arrived via a "Match agent → Hire" flow, the brief is carried
 * via router state (fast) and also as a `?brief=<id>` URL param (survives
 * refresh / new-tab). The panel receives the brief and skips scanning,
 * pitching directly against the client's actual name, company and budget.
 */
export default function AgentChatPage() {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [brief, setBrief] = useState(location.state?.brief || null);
  const [agent, setAgent] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get(`/agent/${id}`)
      .then((r) => {
        if (alive) setAgent(r.data);
      })
      .catch(() => {
        if (alive) setNotFound(true);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  // Fallback fetch: if we don't already have a brief via router state but the
  // URL contains ?brief=<id>, pull it from the backend. This makes the flow
  // resilient to refresh / new-tab / shared-link opens.
  useEffect(() => {
    if (brief) return;
    const briefId = searchParams.get("brief");
    if (!briefId) return;
    let alive = true;
    api
      .get(`/briefs/${briefId}`)
      .then((r) => {
        if (alive) setBrief(r.data);
      })
      .catch(() => {
        // Silent — falls back to the non-brief flow
      });
    return () => {
      alive = false;
    };
  }, [brief, searchParams]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-20 text-center">
        <p className="font-display text-2xl font-black text-slate-900">Agent not found</p>
        <p className="text-slate-500 mt-2 text-sm">
          This agent may have been removed.{" "}
          <Link to="/marketplace" className="text-blue-600 hover:underline">
            Browse the marketplace
          </Link>
          .
        </p>
      </div>
    );
  }

  if (!agent) {
    return (
      <div
        data-testid="agent-chat-page"
        className="mx-auto max-w-3xl px-5 py-20 text-center text-slate-500"
      >
        Loading agent…
      </div>
    );
  }

  return (
    <div
      data-testid="agent-chat-page"
      className="mx-auto max-w-3xl px-5 md:px-8 pt-8 pb-20"
    >
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-5">
        <Link to="/marketplace" className="inline-flex items-center gap-1 hover:text-blue-600">
          <ArrowLeft size={14} /> Marketplace
        </Link>
        <span>/</span>
        <span className="text-slate-900 truncate">{agent.name}</span>
      </div>

      {/* Tiny intro line above the panel */}
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-600 mb-2 inline-flex items-center gap-1.5">
        <span className="relative inline-flex w-1.5 h-1.5">
          <span className="absolute inset-0 rounded-full bg-blue-500/50 animate-ping" />
          <span className="relative inline-block w-1.5 h-1.5 rounded-full bg-blue-500" />
        </span>
        {brief ? "Replying to your brief" : "Live preview"}
      </p>
      <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900 mb-4">
        {brief
          ? `${agent.name} is drafting a proposal for you.`
          : `${agent.name} · earning while you watch.`}
      </h1>

      {brief && (
        <div
          data-testid="brief-context-banner"
          className="mb-4 rounded-2xl border border-blue-200 bg-blue-50/60 px-4 py-3 flex items-start gap-3"
        >
          <span className="w-8 h-8 rounded-full bg-blue-600 text-white grid place-items-center shrink-0">
            <Briefcase size={14} strokeWidth={2.4} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700">
              Your brief
            </p>
            <p className="font-display text-[14px] font-black tracking-tight text-slate-900 truncate">
              {brief.title}
            </p>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600">
              <span className="font-bold text-slate-700">{brief.category}</span>
              <span className="inline-flex items-center gap-1">
                <IndianRupee size={10} className="text-emerald-600" strokeWidth={2.6} />
                {brief.budget_min.toLocaleString("en-IN")}–{brief.budget_max.toLocaleString("en-IN")}
              </span>
              <span className="inline-flex items-center gap-1">
                <Clock size={10} className="text-slate-500" strokeWidth={2.4} />
                {brief.timeline_days}d
              </span>
            </div>
          </div>
        </div>
      )}

      <AgentActionChatPanel agent={agent} brief={brief} active={true} />

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <Link
          to="/marketplace"
          data-testid="back-to-marketplace-btn"
          className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white text-sm font-medium px-5 py-3 text-slate-700 hover:border-blue-400 hover:text-blue-700"
        >
          <ArrowLeft size={15} /> Back to marketplace
        </Link>
        <Link
          to="/build"
          data-testid="build-your-own-btn"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 text-white text-sm font-medium px-5 py-3 hover:bg-blue-700 shadow-[0_14px_30px_-14px_rgba(37,99,235,0.7)]"
        >
          Build my own agent
        </Link>
      </div>
    </div>
  );
}
